import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import AppShell from "../components/AppShell";
import { auth, db } from "../firebase";
import "../styles/admin.css";

const DEPARTMENT_MAP = {
  "AE": "Aerospace Engineering",
  "BSBE": "Biological Sciences & Bioengineering",
  "CHE": "Chemical Engineering",
  "CE": "Civil Engineering",
  "CSE": "Computer Science & Engineering",
  "EE": "Electrical Engineering",
  "MSE": "Materials Science & Engineering",
  "ME": "Mechanical Engineering",
  "CHM": "Chemistry",
  "MTH": "Mathematics & Statistics",
  "PHY": "Physics",
  "ES": "Earth Sciences",
  "ECO": "Economic Sciences"
};

const DEPARTMENTS = [
  { code: "AE", name: "Aerospace Engineering" },
  { code: "BSBE", name: "Biological Sciences & Bioengineering" },
  { code: "CHE", name: "Chemical Engineering" },
  { code: "CE", name: "Civil Engineering" },
  { code: "CSE", name: "Computer Science & Engineering" },
  { code: "EE", name: "Electrical Engineering" },
  { code: "MSE", name: "Materials Science & Engineering" },
  { code: "ME", name: "Mechanical Engineering" },
  { code: "CHM", name: "Chemistry" },
  { code: "MTH", name: "Mathematics & Statistics" },
  { code: "PHY", name: "Physics" },
  { code: "ES", name: "Earth Sciences" },
  { code: "ECO", name: "Economic Sciences" }
];

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checkingRole, setCheckingRole] = useState(true);
  const [activeTab, setActiveTab] = useState("projects"); // "projects" | "resumes" | "applications"

  // Edit project
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Lists
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);

  // Filters
  const [projectFilter, setProjectFilter] = useState("pending");
  const [resumeFilter, setResumeFilter] = useState("pending");
  const [applicationFilter, setApplicationFilter] = useState("pending");
  const [projectSearch, setProjectSearch] = useState("");
  const [resumeSearch, setResumeSearch] = useState("");
  const [applicationSearch, setApplicationSearch] = useState("");

  // Status & Error
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadProjects = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "projects"));
      const projectsList = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setProjects(projectsList);
    } catch (err) {
      console.error("Error loading projects: ", err);
      setError("Failed to load projects: " + err.message);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const usersList = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setUsers(usersList);
    } catch (err) {
      console.error("Error loading users: ", err);
      setError("Failed to load user profiles: " + err.message);
    }
  }, []);

  const loadApplications = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "applications"));
      const appList = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setApplications(appList);
    } catch (err) {
      console.error("Error loading applications: ", err);
      setError("Failed to load applications: " + err.message);
    }
  }, []);

  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError("");
    setSuccess("");
    try {
      await Promise.all([loadProjects(), loadUsers(), loadApplications()]);
      setSuccess("Dashboard data refreshed successfully.");
    } catch (err) {
      console.error("Refresh error: ", err);
      setError("Failed to refresh dashboard: " + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const verifyRole = useCallback(async (currentUser) => {
    try {
      setCheckingRole(true);
      const tokenResult = await currentUser.getIdTokenResult();
      const isAdmin = tokenResult.claims.isAdmin === true;

      if (!isAdmin) {
        await signOut(auth);
        navigate("/admin/login");
        return;
      }

      await Promise.all([loadProjects(), loadUsers(), loadApplications()]);
    } catch (err) {
      console.error("Error verifying admin role:", err);
      setError("Authorization verification error: " + err.message);
    } finally {
      setCheckingRole(false);
      setLoading(false);
    }
  }, [loadProjects, loadUsers, loadApplications, navigate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/admin/login");
        return;
      }
      await verifyRole(currentUser);
    });

    return () => unsubscribe();
  }, [verifyRole, navigate]);

  const handleUpdateProjectStatus = async (projectId, newStatus) => {
    setError("");
    setSuccess("");
    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        publishingStatus: newStatus,
      });
      setSuccess(`Project ${newStatus} successfully.`);
      await loadProjects();
    } catch (err) {
      console.error("Failed to update project status:", err);
      setError("Failed to update project status: " + err.message);
    }
  };

  const handleEditProject = (proj) => {
    setEditingProjectId(proj.id);
    setEditForm({
      title: proj.title || "",
      description: proj.description || "",
      stipendType: proj.stipendType || (proj.compensation === "Unpaid" ? "Unpaid" : "Paid"),
      stipendMin: proj.stipendMin !== undefined && proj.stipendMin !== null ? proj.stipendMin.toString() : "",
      stipendMax: proj.stipendMax !== undefined && proj.stipendMax !== null ? proj.stipendMax.toString() : "",
      location: proj.location || "Onsite",
      timeCommitment: proj.timeCommitment || "",
      timeline: proj.timeline || "",
      deadline: proj.deadline || "",
      openings: proj.openings ?? 1,
      cpiRequirement: proj.cpiRequirement !== undefined && proj.cpiRequirement !== null ? proj.cpiRequirement.toString() : "",
      priorExperience: proj.priorExperience || "",
      status: proj.status || "open",
      eligibleYears: proj.eligibleYears || [],
      requiredDocuments: proj.requiredDocuments || [],
      departments: proj.departments || [],
      openToAll: proj.openToAll !== undefined ? proj.openToAll : (proj.departments && proj.departments.length > 0 ? false : true),
      optionalTags: proj.optionalTags || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingProjectId(null);
    setEditForm({});
  };

  const handleSaveProjectEdit = async (projectId) => {
    setEditSaving(true);
    setError("");
    setSuccess("");
    try {
      const depts = editForm.openToAll ? [] : editForm.departments;

      let compText = "Unpaid";
      if (editForm.stipendType === "Paid") {
        compText = editForm.stipendMin
          ? `${editForm.stipendMin} / mo`
          : "Paid";
      }

      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        stipendType: editForm.stipendType,
        stipendMin: editForm.stipendMin ? parseFloat(editForm.stipendMin) : null,
        stipendMax: null,
        compensation: compText,
        location: editForm.location,
        timeCommitment: editForm.timeCommitment.trim(),
        timeline: editForm.timeline.trim(),
        deadline: editForm.deadline || null,
        openings: parseInt(editForm.openings) || 1,
        cpiRequirement: editForm.cpiRequirement !== "" && editForm.cpiRequirement !== undefined && editForm.cpiRequirement !== null ? parseFloat(editForm.cpiRequirement) : null,
        priorExperience: editForm.priorExperience.trim(),
        status: editForm.status,
        eligibleYears: editForm.eligibleYears || [],
        requiredDocuments: editForm.requiredDocuments || [],
        departments: depts,
        openToAll: editForm.openToAll,
        optionalTags: editForm.optionalTags || "",
      });
      setSuccess("Project updated successfully.");
      setEditingProjectId(null);
      setEditForm({});
      await loadProjects();
    } catch (err) {
      console.error("Failed to save project edits:", err);
      setError("Failed to save project: " + err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleUpdateResumeStatus = async (studentId, resumeUrl, newStatus) => {
    setError("");
    setSuccess("");
    try {
      const studentRef = doc(db, "users", studentId);
      const studentSnap = await getDoc(studentRef);
      if (!studentSnap.exists()) {
        setError("Student not found.");
        return;
      }

      const studentData = studentSnap.data();
      const currentResumes = studentData.resumes || [];

      const updatedResumes = currentResumes.map((resume) => {
        if (resume.url === resumeUrl) {
          return {
            ...resume,
            approvalStatus: newStatus,
          };
        }
        return resume;
      });

      await updateDoc(studentRef, { resumes: updatedResumes });
      setSuccess(`Resume status updated to ${newStatus} successfully.`);
      await loadUsers();
    } catch (err) {
      console.error("Failed to update resume status:", err);
      setError("Failed to update resume status: " + err.message);
    }
  };

  const handleUpdateApplicationApproval = async (applicationId, newStatus) => {
    setError("");
    setSuccess("");
    try {
      const appRef = doc(db, "applications", applicationId);
      await updateDoc(appRef, {
        adminApprovalStatus: newStatus,
      });
      setSuccess(`Application ${newStatus} successfully.`);
      await loadApplications();
    } catch (err) {
      console.error("Failed to update application approval:", err);
      setError("Failed to update application: " + err.message);
    }
  };

  const handleUpdateApplicationProfessorStatus = async (applicationId, newStatus) => {
    setError("");
    setSuccess("");
    try {
      const appRef = doc(db, "applications", applicationId);
      await updateDoc(appRef, {
        status: newStatus,
      });
      setSuccess(`Professor Decision status updated to ${newStatus}.`);
      await loadApplications();

      // Notify student via email (non-blocking — email failure does not affect status update)
      try {
        const token = await auth.currentUser.getIdToken();
        await fetch("https://us-central1-fep-iitk.cloudfunctions.net/api/notify-status-change", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ applicationId, newStatus }),
        });
      } catch (emailErr) {
        console.warn("Could not send notification email:", emailErr);
      }
    } catch (err) {
      console.error("Failed to update application status:", err);
      setError("Failed to update application status: " + err.message);
    }
  };

  // ─── Helper lookups ───────────────────────────────────────────────────────
  const getProjectTitle = (projectId) => {
    const proj = projects.find((p) => p.id === projectId);
    return proj?.title || projectId;
  };

  const getProfessorDept = (professorId) => {
    const prof = users.find((u) => u.id === professorId);
    if (!prof) return "N/A";
    const dept1 = prof.department1;
    const dept2 = prof.department2;
    if (dept1 && dept2) return `${dept1}, ${dept2}`;
    return dept1 || dept2 || "N/A";
  };

  // ─── Filter Logic ─────────────────────────────────────────────────────────
  const filteredProjects = projects.filter((proj) => {
    const status = proj.publishingStatus || "approved";
    const matchesStatus = projectFilter === "all" || status === projectFilter;
    const searchLower = projectSearch.toLowerCase();
    const matchesSearch =
      proj.title.toLowerCase().includes(searchLower) ||
      (proj.createdByName || "").toLowerCase().includes(searchLower);
    return matchesStatus && matchesSearch;
  });

  const students = users.filter((u) => u.role === "student");

  const resumesList = [];
  students.forEach((student) => {
    const currentResumes = student.resumes || [];
    currentResumes.forEach((res) => {
      let deptStr = "N/A";
      if (student.department1 && student.department2) {
        deptStr = `${student.department1}, ${student.department2}`;
      } else {
        deptStr = student.department1 || student.department2 || "N/A";
      }
      resumesList.push({
        ...res,
        studentId: student.id,
        studentName: student.fullName || student.name || "Unknown Student",
        studentRoll: student.roll || "N/A",
        studentDept: deptStr,
      });
    });
  });

  const filteredResumes = resumesList.filter((resume) => {
    const status = resume.approvalStatus || "approved";
    const matchesStatus = resumeFilter === "all" || status === resumeFilter;
    const searchLower = resumeSearch.toLowerCase();
    const matchesSearch =
      resume.studentName.toLowerCase().includes(searchLower) ||
      (resume.studentRoll || "").toLowerCase().includes(searchLower);
    return matchesStatus && matchesSearch;
  });

  const filteredApplications = applications.filter((app) => {
    const status = app.adminApprovalStatus || "pending";
    const matchesStatus =
      applicationFilter === "all" || status === applicationFilter;
    const searchLower = applicationSearch.toLowerCase();
    const projectTitle = getProjectTitle(app.projectId).toLowerCase();
    const studentName = (app.studentName || "").toLowerCase();
    const matchesSearch =
      studentName.includes(searchLower) || projectTitle.includes(searchLower);
    return matchesStatus && matchesSearch;
  });

  // Counts for tab labels
  const pendingProjectsCount = projects.filter(
    (p) => !p.publishingStatus || p.publishingStatus === "pending"
  ).length;

  const pendingResumesCount = resumesList.filter(
    (r) => !r.approvalStatus || r.approvalStatus === "pending"
  ).length;

  const pendingApplicationsCount = applications.filter(
    (a) => !a.adminApprovalStatus || a.adminApprovalStatus === "pending"
  ).length;

  if (loading || checkingRole) {
    return (
      <AppShell showSearch={false}>
        <div className="admin-loading-container">Verifying credentials…</div>
      </AppShell>
    );
  }

  return (
    <AppShell showSearch={false}>
      <div className="admin-page shell-page">

        {/* ── Page Header ── */}
        <div className="admin-page-header">
          <div className="admin-page-header-text">
            <p className="admin-kicker">CONTROL PANEL</p>
            <h1>System Administrator</h1>
            <p>Approve or reject projects, student resumes, and applications.</p>
          </div>
          <button
            onClick={handleRefresh}
            className="btn-refresh-dashboard"
            disabled={refreshing}
          >
            <span className={refreshing ? "spinner-icon" : ""}>↻</span>{" "}
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {error && <div className="admin-message-alert admin-error">{error}</div>}
        {success && <div className="admin-message-alert admin-success">{success}</div>}

        {/* ── Tabs ── */}
        <div className="admin-tabs">
          <button
            className={`admin-tab-btn ${activeTab === "projects" ? "active" : ""}`}
            onClick={() => setActiveTab("projects")}
          >
            Manage Projects
            {pendingProjectsCount > 0 && (
              <span className="admin-tab-badge">{pendingProjectsCount}</span>
            )}
          </button>
          <button
            className={`admin-tab-btn ${activeTab === "resumes" ? "active" : ""}`}
            onClick={() => setActiveTab("resumes")}
          >
            Manage Resumes
            {pendingResumesCount > 0 && (
              <span className="admin-tab-badge">{pendingResumesCount}</span>
            )}
          </button>
          <button
            className={`admin-tab-btn ${activeTab === "applications" ? "active" : ""}`}
            onClick={() => setActiveTab("applications")}
          >
            Applications
            {pendingApplicationsCount > 0 && (
              <span className="admin-tab-badge">{pendingApplicationsCount}</span>
            )}
          </button>
        </div>

        {/* ── Tab 1: PROJECTS ── */}
        {activeTab === "projects" && (
          <section className="admin-section">
            <div className="admin-section-header">
              <h2>Professor Projects</h2>
              <div className="admin-header-actions">
                <input
                  type="text"
                  placeholder="Search by project or professor…"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  className="admin-search-input"
                />
                <div className="admin-filters">
                  <button
                    className={projectFilter === "pending" ? "active" : ""}
                    onClick={() => setProjectFilter("pending")}
                  >
                    Pending
                  </button>
                  <button
                    className={projectFilter === "approved" ? "active" : ""}
                    onClick={() => setProjectFilter("approved")}
                  >
                    Approved
                  </button>
                  <button
                    className={projectFilter === "rejected" ? "active" : ""}
                    onClick={() => setProjectFilter("rejected")}
                  >
                    Rejected
                  </button>
                  <button
                    className={projectFilter === "all" ? "active" : ""}
                    onClick={() => setProjectFilter("all")}
                  >
                    All
                  </button>
                </div>
              </div>
            </div>

            {filteredProjects.length === 0 ? (
              <div className="admin-empty-state">No projects found for the selected status.</div>
            ) : (
              <div className="admin-grid">
                {filteredProjects.map((proj) => {
                  const status = proj.publishingStatus || "approved";
                  const isEditing = editingProjectId === proj.id;
                  return (
                    <div key={proj.id} className="admin-card">
                      <div className="admin-card-header">
                        <h3>{proj.title}</h3>
                        <span className={`admin-badge badge-${status}`}>
                          {status}
                        </span>
                      </div>

                      {!isEditing && (
                        <>
                          <div className="admin-card-meta">
                            <div className="meta-full-width"><strong>Professor:</strong> {proj.createdByName || "N/A"}</div>
                            <div className="meta-full-width"><strong>Department:</strong> {getProfessorDept(proj.createdBy)}</div>
                            <div className="meta-full-width">
                              <strong>Target Depts:</strong> {proj.openToAll || !proj.departments || proj.departments.length === 0 ? "Open to all departments" : proj.departments.map(code => DEPARTMENT_MAP[code] || code).join(", ")}
                            </div>
                            <div><strong>Stipend:</strong> {proj.compensation || "N/A"}</div>
                            <div><strong>Location:</strong> {proj.location || "N/A"}</div>
                            <div><strong>Commitment:</strong> {proj.timeCommitment || "N/A"}</div>
                            <div><strong>Timeline:</strong> {proj.timeline || "N/A"}</div>
                            <div><strong>Min CPI:</strong> {proj.cpiRequirement || "None"}</div>
                            <div><strong>Openings:</strong> {proj.openings || 1}</div>
                            <div><strong>Project Status:</strong> <span style={{ textTransform: 'capitalize' }}>{proj.status || "Open"}</span></div>
                            {proj.deadline && (
                              <div><strong>Deadline:</strong> {new Date(proj.deadline).toLocaleDateString()}</div>
                            )}
                            {proj.eligibleYears && proj.eligibleYears.length > 0 && (
                              <div className="meta-full-width"><strong>Eligible Years:</strong> {proj.eligibleYears.join(", ")}</div>
                            )}
                            {proj.requiredDocuments && proj.requiredDocuments.length > 0 && (
                              <div className="meta-full-width"><strong>Required Docs:</strong> {proj.requiredDocuments.join(", ")}</div>
                            )}
                            {proj.optionalTags && (
                              <div className="meta-full-width"><strong>Tags:</strong> {proj.optionalTags}</div>
                            )}
                          </div>

                          <div className="admin-card-body">
                            <strong>Description:</strong>
                            <p>{proj.description}</p>
                            {proj.priorExperience && (
                              <>
                                <strong>Prior Experience Required:</strong>
                                <p>{proj.priorExperience}</p>
                              </>
                            )}
                          </div>
                        </>
                      )}

                      {isEditing && (
                        <div className="admin-edit-form">
                          <div className="admin-edit-field">
                            <label>Title</label>
                            <input
                              type="text"
                              value={editForm.title}
                              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                              className="admin-edit-input"
                            />
                          </div>
                          <div className="admin-edit-field">
                            <label>Description</label>
                            <textarea
                              value={editForm.description}
                              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                              className="admin-edit-textarea"
                              rows={4}
                            />
                          </div>

                          <div className="admin-edit-row">
                            <div className="admin-edit-field">
                              <label>Stipend Type</label>
                              <select
                                value={editForm.stipendType}
                                onChange={(e) => setEditForm({
                                  ...editForm,
                                  stipendType: e.target.value,
                                  stipendMin: e.target.value === "Unpaid" ? "" : editForm.stipendMin,
                                  stipendMax: e.target.value === "Unpaid" ? "" : editForm.stipendMax
                                })}
                                className="admin-edit-input"
                              >
                                <option value="Paid">Paid</option>
                                <option value="Unpaid">Unpaid</option>
                              </select>
                            </div>
                            <div className="admin-edit-field">
                              <label>Location</label>
                              <select
                                value={editForm.location}
                                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                                className="admin-edit-input"
                              >
                                <option value="Onsite">Onsite</option>
                                <option value="Remote">Remote</option>
                                <option value="Hybrid">Hybrid</option>
                              </select>
                            </div>
                          </div>

                          {editForm.stipendType === "Paid" && (
                            <div className="admin-edit-field">
                              <label>Stipend (INR/mo)</label>
                              <input
                                type="number"
                                placeholder="Enter stipend amount"
                                value={editForm.stipendMin}
                                onChange={(e) => setEditForm({ ...editForm, stipendMin: e.target.value })}
                                className="admin-edit-input"
                              />
                            </div>
                          )}

                          <div className="admin-edit-row">
                            <div className="admin-edit-field">
                              <label>Time Commitment</label>
                              <input
                                type="text"
                                value={editForm.timeCommitment}
                                onChange={(e) => setEditForm({ ...editForm, timeCommitment: e.target.value })}
                                className="admin-edit-input"
                              />
                            </div>
                            <div className="admin-edit-field">
                              <label>Timeline</label>
                              <input
                                type="text"
                                value={editForm.timeline}
                                onChange={(e) => setEditForm({ ...editForm, timeline: e.target.value })}
                                className="admin-edit-input"
                              />
                            </div>
                          </div>

                          <div className="admin-edit-row">
                            <div className="admin-edit-field">
                              <label>Deadline</label>
                              <input
                                type="date"
                                value={editForm.deadline}
                                onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
                                className="admin-edit-input"
                              />
                            </div>
                            <div className="admin-edit-field">
                              <label>Openings</label>
                              <input
                                type="number"
                                min="1"
                                value={editForm.openings}
                                onChange={(e) => setEditForm({ ...editForm, openings: e.target.value })}
                                className="admin-edit-input"
                              />
                            </div>
                          </div>

                          <div className="admin-edit-row">
                            <div className="admin-edit-field">
                              <label>Min CPI</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="10"
                                value={editForm.cpiRequirement}
                                onChange={(e) => setEditForm({ ...editForm, cpiRequirement: e.target.value })}
                                className="admin-edit-input"
                                placeholder="Leave blank for none"
                              />
                            </div>
                            <div className="admin-edit-field">
                              <label>Status</label>
                              <select
                                value={editForm.status}
                                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                className="admin-edit-input"
                              >
                                <option value="open">Open</option>
                                <option value="closed">Closed</option>
                              </select>
                            </div>
                          </div>

                          <div className="admin-edit-field">
                            <label>Prior Experience Required</label>
                            <input
                              type="text"
                              value={editForm.priorExperience}
                              onChange={(e) => setEditForm({ ...editForm, priorExperience: e.target.value })}
                              className="admin-edit-input"
                              placeholder="e.g. Basic Python, ML fundamentals"
                            />
                          </div>

                          <div className="admin-edit-field">
                            <label style={{ fontWeight: '600', marginBottom: '2px' }}>Eligible Years</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '4px 0' }}>
                              {["1st Year", "2nd Year", "3rd Year", "4th Year"].map((year) => {
                                const isChecked = (editForm.eligibleYears || []).includes(year);
                                return (
                                  <label key={year} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 'normal' }}>
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        const current = editForm.eligibleYears || [];
                                        const next = e.target.checked
                                          ? [...current, year]
                                          : current.filter((y) => y !== year);
                                        setEditForm({ ...editForm, eligibleYears: next });
                                      }}
                                    />
                                    {year}
                                  </label>
                                );
                              })}
                            </div>
                          </div>

                          <div className="admin-edit-field">
                            <label style={{ fontWeight: '600', marginBottom: '2px' }}>Required Documents</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px', padding: '4px 0' }}>
                              {["Resume/CV", "Cover Letter", "Statement of Purpose (SOP)", "Academic Transcripts", "Recommendation Letter"].map((docName) => {
                                const isChecked = (editForm.requiredDocuments || []).includes(docName);
                                return (
                                  <label key={docName} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 'normal' }}>
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        const current = editForm.requiredDocuments || [];
                                        const next = e.target.checked
                                          ? [...current, docName]
                                          : current.filter((d) => d !== docName);
                                        setEditForm({ ...editForm, requiredDocuments: next });
                                      }}
                                    />
                                    {docName}
                                  </label>
                                );
                              })}
                            </div>
                          </div>

                          <div className="admin-edit-field" style={{ margin: '4px 0' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editForm.openToAll}
                                onChange={(e) => setEditForm({
                                  ...editForm,
                                  openToAll: e.target.checked,
                                  departments: e.target.checked ? [] : editForm.departments
                                })}
                              />
                              <strong>Open to all departments</strong>
                            </label>
                          </div>

                          {!editForm.openToAll && (
                            <div className="admin-edit-field">
                              <label>Eligible Departments</label>
                              <select
                                value=""
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val && !(editForm.departments || []).includes(val)) {
                                    const next = [...(editForm.departments || []), val];
                                    setEditForm({ ...editForm, departments: next });
                                  }
                                  e.target.value = "";
                                }}
                                className="admin-edit-input"
                              >
                                <option value="">-- Select Department --</option>
                                {DEPARTMENTS.map((dept) => (
                                  <option key={dept.code} value={dept.code} disabled={(editForm.departments || []).includes(dept.code)}>
                                    {dept.name}
                                  </option>
                                ))}
                              </select>

                              {(editForm.departments || []).length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                                  {editForm.departments.map((code) => {
                                    const name = DEPARTMENT_MAP[code] || code;
                                    return (
                                      <span
                                        key={code}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          padding: '3px 8px',
                                          background: 'var(--app-surface-hover, #edf2f7)',
                                          color: 'var(--app-ink, #2d3748)',
                                          borderRadius: '12px',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          border: '1px solid var(--app-border, #cbd5e1)'
                                        }}
                                      >
                                        {name}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const next = editForm.departments.filter((d) => d !== code);
                                            setEditForm({ ...editForm, departments: next });
                                          }}
                                          style={{
                                            border: 'none',
                                            background: 'transparent',
                                            color: '#ef4444',
                                            fontWeight: 'bold',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            padding: '0 2px',
                                            display: 'inline-flex',
                                            alignItems: 'center'
                                          }}
                                        >
                                          ✕
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="admin-edit-field">
                            <label>Optional Tags (comma-separated)</label>
                            <input
                              type="text"
                              placeholder="e.g. Machine Learning, NLP, Python"
                              value={editForm.optionalTags}
                              onChange={(e) => setEditForm({ ...editForm, optionalTags: e.target.value })}
                              className="admin-edit-input"
                            />
                          </div>
                        </div>
                      )}

                      <div className="admin-card-actions">
                        {!isEditing && (
                          <button
                            onClick={() => handleEditProject(proj)}
                            className="admin-btn-action admin-btn-edit"
                          >
                            ✎ Edit Details
                          </button>
                        )}
                        {isEditing && (
                          <>
                            <button
                              onClick={() => handleSaveProjectEdit(proj.id)}
                              className="admin-btn-action admin-btn-approve"
                              disabled={editSaving}
                            >
                              {editSaving ? "Saving…" : "✓ Save Changes"}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="admin-btn-action admin-btn-undo"
                              disabled={editSaving}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {!isEditing && status !== "approved" && (
                          <button
                            onClick={() => handleUpdateProjectStatus(proj.id, "approved")}
                            className="admin-btn-action admin-btn-approve"
                          >
                            Approve
                          </button>
                        )}
                        {!isEditing && status !== "rejected" && (
                          <button
                            onClick={() => handleUpdateProjectStatus(proj.id, "rejected")}
                            className="admin-btn-action admin-btn-reject"
                          >
                            Reject
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ── Tab 2: RESUMES ── */}
        {activeTab === "resumes" && (
          <section className="admin-section">
            <div className="admin-section-header">
              <h2>Student Resumes</h2>
              <div className="admin-header-actions">
                <input
                  type="text"
                  placeholder="Search by student name or roll…"
                  value={resumeSearch}
                  onChange={(e) => setResumeSearch(e.target.value)}
                  className="admin-search-input"
                />
                <div className="admin-filters">
                  <button
                    className={resumeFilter === "pending" ? "active" : ""}
                    onClick={() => setResumeFilter("pending")}
                  >
                    Pending
                  </button>
                  <button
                    className={resumeFilter === "approved" ? "active" : ""}
                    onClick={() => setResumeFilter("approved")}
                  >
                    Approved
                  </button>
                  <button
                    className={resumeFilter === "rejected" ? "active" : ""}
                    onClick={() => setResumeFilter("rejected")}
                  >
                    Rejected
                  </button>
                  <button
                    className={resumeFilter === "all" ? "active" : ""}
                    onClick={() => setResumeFilter("all")}
                  >
                    All
                  </button>
                </div>
              </div>
            </div>

            {filteredResumes.length === 0 ? (
              <div className="admin-empty-state">No resumes found for the selected status.</div>
            ) : (
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Resume Name</th>
                      <th>Student</th>
                      <th>Roll Number</th>
                      <th>Department</th>
                      <th>Upload Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResumes.map((resume, idx) => {
                      const status = resume.approvalStatus || "approved";
                      return (
                        <tr key={idx}>
                          <td>
                            <div className="resume-name-col">
                              <span className="file-emoji">📄</span>
                              <span>{resume.name}</span>
                            </div>
                          </td>
                          <td><strong>{resume.studentName}</strong></td>
                          <td>{resume.studentRoll}</td>
                          <td>{resume.studentDept}</td>
                          <td>{resume.uploadedAt ? new Date(resume.uploadedAt).toLocaleDateString() : "N/A"}</td>
                          <td>
                            <span className={`admin-badge badge-${status}`}>
                              {status}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              <a
                                href={resume.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-link-view"
                              >
                                View PDF
                              </a>
                              {status !== "approved" && (
                                <button
                                  onClick={() => handleUpdateResumeStatus(resume.studentId, resume.url, "approved")}
                                  className="admin-table-btn btn-table-approve"
                                >
                                  Approve
                                </button>
                              )}
                              {status !== "rejected" && (
                                <button
                                  onClick={() => handleUpdateResumeStatus(resume.studentId, resume.url, "rejected")}
                                  className="admin-table-btn btn-table-reject"
                                >
                                  Reject
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ── Tab 3: APPLICATIONS ── */}
        {activeTab === "applications" && (
          <section className="admin-section">
            <div className="admin-section-header">
              <h2>Student Applications</h2>
              <div className="admin-header-actions">
                <input
                  type="text"
                  placeholder="Search by student or project…"
                  value={applicationSearch}
                  onChange={(e) => setApplicationSearch(e.target.value)}
                  className="admin-search-input"
                />
                <div className="admin-filters">
                  <button
                    className={applicationFilter === "pending" ? "active" : ""}
                    onClick={() => setApplicationFilter("pending")}
                  >
                    Pending
                  </button>
                  <button
                    className={applicationFilter === "approved" ? "active" : ""}
                    onClick={() => setApplicationFilter("approved")}
                  >
                    Approved
                  </button>
                  <button
                    className={applicationFilter === "rejected" ? "active" : ""}
                    onClick={() => setApplicationFilter("rejected")}
                  >
                    Rejected
                  </button>
                  <button
                    className={applicationFilter === "all" ? "active" : ""}
                    onClick={() => setApplicationFilter("all")}
                  >
                    All
                  </button>
                </div>
              </div>
            </div>

            {filteredApplications.length === 0 ? (
              <div className="admin-empty-state">
                {applicationFilter === "pending"
                  ? "No applications pending review. All caught up! 🎉"
                  : "No applications found for the selected filter."}
              </div>
            ) : (
              <div className="admin-grid">
                {filteredApplications.map((app) => {
                  const adminStatus = app.adminApprovalStatus || "pending";
                  const projectTitle = getProjectTitle(app.projectId);
                  return (
                    <div key={app.id} className="admin-card admin-app-card">
                      <div className="admin-card-header">
                        <div className="admin-app-card-title">
                          <span className="admin-app-avatar">
                            {(app.studentName || "S").charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <h3>{app.studentName || "Unknown Student"}</h3>
                            <p className="admin-app-project-name">{projectTitle}</p>
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
                          <span className={`admin-badge badge-${adminStatus}`}>
                            Admin: {adminStatus}
                          </span>
                          {adminStatus === "approved" && (
                            <span className={`admin-badge status-${app.status || "applied"}`}>
                              Prof: {app.status || "applied"}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="admin-card-meta">
                        <div><strong>CPI:</strong> {app.cpi || "N/A"}</div>
                        <div><strong>Applied:</strong> {app.createdAt ? new Date(app.createdAt.toDate?.() || app.createdAt).toLocaleDateString() : "N/A"}</div>
                        {app.experience && (
                          <div className="meta-full-width">
                            <strong>Experience:</strong> {app.experience}
                          </div>
                        )}
                      </div>

                      {app.coverNote && (
                        <div className="admin-card-body">
                          <strong>Cover Note:</strong>
                          <p>{app.coverNote}</p>
                        </div>
                      )}

                      <div className="admin-app-card-links">
                        {app.resumeUrl && (
                          <a
                            href={app.resumeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-link-view"
                          >
                            📄 View Resume
                          </a>
                        )}
                      </div>

                      <div className="admin-card-actions">
                        {adminStatus !== "approved" && (
                          <button
                            onClick={() => handleUpdateApplicationApproval(app.id, "approved")}
                            className="admin-btn-action admin-btn-approve"
                          >
                            ✓ Approve & Forward
                          </button>
                        )}
                        {adminStatus !== "rejected" && (
                          <button
                            onClick={() => handleUpdateApplicationApproval(app.id, "rejected")}
                            className="admin-btn-action admin-btn-reject"
                          >
                            ✕ Reject
                          </button>
                        )}
                        {adminStatus === "approved" && (
                          <button
                            onClick={() => handleUpdateApplicationApproval(app.id, "pending")}
                            className="admin-btn-action admin-btn-undo"
                          >
                            ↩ Move to Pending
                          </button>
                        )}
                      </div>

                      {adminStatus === "approved" && (
                        <div className="admin-prof-status-section">
                          <strong>Professor Decision:</strong>
                          <select
                            value={app.status || "applied"}
                            onChange={(e) => handleUpdateApplicationProfessorStatus(app.id, e.target.value)}
                            className="admin-edit-input"
                          >
                            <option value="applied">Applied</option>
                            <option value="shortlisted">Shortlisted</option>
                            <option value="selected">Selected</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

      </div>
    </AppShell>
  );
};

export default Admin;
